import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

const ExamplePhotoManager = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    loadPhotos();
  }, []);

  const loadPhotos = async () => {
    try {
      setLoading(true);
      const data = await apiService.getExamplePhotos();
      setPhotos(data.photos || []);
    } catch (err) {
      setError('Failed to load example photos');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e, category) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    try {
      setLoading(true);
      for (let i = 0; i < files.length; i++) {
        await apiService.uploadExamplePhoto(files[i], category);
      }
      await loadPhotos();
    } catch (err) {
      alert('Failed to upload photo(s)');
    } finally {
      setLoading(false);
    }
    e.target.value = '';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this example photo?')) return;
    try {
      setLoading(true);
      await apiService.deleteExamplePhoto(id);
      await loadPhotos();
    } catch (err) {
      alert('Failed to delete photo');
    } finally {
      setLoading(false);
    }
  };

  const POSTA_POSES = [
    { key: 'up',    label: 'Pose 1 — UP',    arrow: '⬆️', cats: ['posta_up_Male', 'posta_up_Female'] },
    { key: 'left',  label: 'Pose 2 — LEFT',  arrow: '⬅️', cats: ['posta_left_Male', 'posta_left_Female'] },
    { key: 'down',  label: 'Pose 3 — DOWN',  arrow: '⬇️', cats: ['posta_down_Male', 'posta_down_Female'] },
    { key: 'right', label: 'Pose 4 — RIGHT', arrow: '➡️', cats: ['posta_right_Male', 'posta_right_Female'] },
  ];

  const POSTB_POSES = [
    { key: 'up_left',    label: 'Pose 5 — UP-LEFT',    arrow: '↖️', cats: ['postb_up_left_Male', 'postb_up_left_Female'] },
    { key: 'up_right',   label: 'Pose 6 — UP-RIGHT',   arrow: '↗️', cats: ['postb_up_right_Male', 'postb_up_right_Female'] },
    { key: 'down_left',  label: 'Pose 7 — DOWN-LEFT',  arrow: '↙️', cats: ['postb_down_left_Male', 'postb_down_left_Female'] },
    { key: 'down_right', label: 'Pose 8 — DOWN-RIGHT', arrow: '↘️', cats: ['postb_down_right_Male', 'postb_down_right_Female'] },
  ];

  const sections = [
    { id: 'historical', title: 'Step 1: Historical Photos', categories: ['Male', 'Female'], labels: ['Male', 'Female'] },
    { id: 'present', title: 'Step 2: Present Neutral Photos', categories: ['present_Male', 'present_Female'], labels: ['Male', 'Female'] },
  ];


  return (
    <div className="example-photo-manager" style={{ marginTop: '2rem', padding: '1.5rem', background: '#1e293b', borderRadius: '12px', border: '1px solid #334155' }}>
      <h3 style={{ marginBottom: '1rem', color: '#f8fafc' }}>📸 Manage Example Photos for All Steps</h3>
      <p style={{ color: '#94a3b8', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        Upload example images for each verification step. These will be shown on the user's upload page as references.
      </p>
      {error && <p style={{ color: '#ef4444' }}>{error}</p>}
      {loading && <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>Loading...</p>}

      {/* Simple sections: Historical + Present */}
      {sections.map(sec => {
        const secPhotos = photos.filter(p => {
          const cat = (p.category || '').toLowerCase();
          const secCats = sec.categories.map(c => c.toLowerCase());
          if (secCats.includes(cat)) return true;
          let altCat1 = cat.replace('_female', '_Female').replace('_girl', '_Female');
          let altCat2 = cat.replace('_male', '_Male');
          if (secCats.includes(altCat1) || secCats.includes(altCat2)) return true;
          if (sec.id === 'historical') {
            if (['girl', 'female', 'Male', 'male'].includes(cat)) return true;
          } else if (sec.id === 'present') {
            if (['present_girl', 'present_female', 'present_Male', 'present_male'].includes(cat)) return true;
          }
          return false;
        });

        return (
          <div key={sec.id} style={{ marginBottom: '2.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #334155' }}>
            <h4 style={{ color: '#e2e8f0', marginBottom: '1rem', fontSize: '1.1rem' }}>{sec.title}</h4>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              {sec.categories.map((cat, idx) => {
                const isFemale = sec.labels[idx].toLowerCase().includes('female');
                return (
                  <div key={cat}>
                    <label className="view-btn" style={{ display: 'inline-block', cursor: 'pointer', background: isFemale ? '#ec4899' : '#3b82f6', color: 'white', padding: '0.5rem 1rem', borderRadius: '6px' }}>
                      + Upload {sec.labels[idx]} Example
                      <input type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={(e) => handleUpload(e, cat)} />
                    </label>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              {secPhotos.map(p => (
                <div key={p.id} style={{ background: '#0f172a', border: '1px solid #334155', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
                  <img
                    src={`${process.env.REACT_APP_API_URL || `https://api.frinksyn.com/api`}/example-photos/${p.id}/file`}
                    alt={p.category}
                    style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '6px', marginBottom: '0.5rem', cursor: 'pointer' }}
                    onClick={() => setLightboxImage(`${process.env.REACT_APP_API_URL || `https://api.frinksyn.com/api`}/example-photos/${p.id}/file`)}
                  />
                  <p style={{ color: '#f8fafc', fontWeight: 'bold', marginBottom: '0.5rem', fontSize: '0.8rem' }}>{p.category.toUpperCase()}</p>
                  <button
                    onClick={() => handleDelete(p.id)}
                    style={{ background: '#dc2626', color: 'white', border: 'none', padding: '0.3rem 0.8rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
                  >
                    Delete
                  </button>
                </div>
              ))}
              {secPhotos.length === 0 && !loading && <p style={{ color: '#64748b', fontSize: '0.9rem', fontStyle: 'italic' }}>No example photos uploaded for this step yet.</p>}
            </div>
          </div>
        );
      })}

      {/* ---- Variation A: per-pose grid ---- */}
      <div style={{ marginBottom: '2.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #334155' }}>
        <h4 style={{ color: '#e2e8f0', marginBottom: '0.5rem', fontSize: '1.1rem' }}>Step 3: Post Variation A (Angles)</h4>
        <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '1.25rem' }}>Upload one example per pose direction and gender. The photo will automatically appear in the matching pose slot on the user's page.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1.25rem' }}>
          {POSTA_POSES.map(pose => (
            <div key={pose.key} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '12px', overflow: 'hidden' }}>
              {/* Pose header */}
              <div style={{ background: '#1e3a5f', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.3rem' }}>{pose.arrow}</span>
                <strong style={{ color: '#e2e8f0', fontSize: '0.95rem' }}>{pose.label}</strong>
              </div>
              {/* Per-gender slots */}
              {pose.cats.map(cat => {
                const isFemale = cat.endsWith('Female');
                const catPhotos = photos.filter(p => (p.category || '').toLowerCase() === cat.toLowerCase());
                return (
                  <div key={cat} style={{ padding: '0.85rem', borderTop: '1px solid #1e293b' }}>
                    <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.5rem', fontWeight: 600 }}>
                      {isFemale ? '♀ Female' : '♂ Male'}
                    </p>
                    <label style={{ display: 'inline-block', cursor: 'pointer', background: isFemale ? '#be185d' : '#1d4ed8', color: 'white', padding: '0.35rem 0.75rem', borderRadius: '6px', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                      + Upload
                      <input type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={(e) => handleUpload(e, cat)} />
                    </label>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      {catPhotos.length === 0 ? (
                        <div style={{ width: '80px', height: '80px', borderRadius: '8px', border: '2px dashed #334155', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: '1.5rem' }}>{pose.arrow}</div>
                      ) : catPhotos.map(p => (
                        <div key={p.id} style={{ textAlign: 'center' }}>
                          <img
                            src={`${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${p.id}/file`}
                            alt={p.category}
                            style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', border: '2px solid #334155' }}
                            onClick={() => setLightboxImage(`${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${p.id}/file`)}
                          />
                          <button onClick={() => handleDelete(p.id)} style={{ display: 'block', width: '80px', marginTop: '0.3rem', background: '#dc2626', color: 'white', border: 'none', padding: '0.2rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>Delete</button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* ---- Variation B: per-pose grid ---- */}
      <div style={{ marginBottom: '2.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #334155' }}>
        <h4 style={{ color: '#e2e8f0', marginBottom: '0.5rem', fontSize: '1.1rem' }}>Step 4: Post Variation B (Diagonal Angles)</h4>
        <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '1.25rem' }}>Upload one example per diagonal pose direction and gender.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1.25rem' }}>
          {POSTB_POSES.map(pose => (
            <div key={pose.key} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ background: '#1a3a2a', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.3rem' }}>{pose.arrow}</span>
                <strong style={{ color: '#e2e8f0', fontSize: '0.95rem' }}>{pose.label}</strong>
              </div>
              {pose.cats.map(cat => {
                const isFemale = cat.endsWith('Female');
                const catPhotos = photos.filter(p => (p.category || '').toLowerCase() === cat.toLowerCase());
                return (
                  <div key={cat} style={{ padding: '0.85rem', borderTop: '1px solid #1e293b' }}>
                    <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.5rem', fontWeight: 600 }}>
                      {isFemale ? '♀ Female' : '♂ Male'}
                    </p>
                    <label style={{ display: 'inline-block', cursor: 'pointer', background: isFemale ? '#be185d' : '#1d4ed8', color: 'white', padding: '0.35rem 0.75rem', borderRadius: '6px', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                      + Upload
                      <input type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={(e) => handleUpload(e, cat)} />
                    </label>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      {catPhotos.length === 0 ? (
                        <div style={{ width: '80px', height: '80px', borderRadius: '8px', border: '2px dashed #334155', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: '1.5rem' }}>{pose.arrow}</div>
                      ) : catPhotos.map(p => (
                        <div key={p.id} style={{ textAlign: 'center' }}>
                          <img
                            src={`${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${p.id}/file`}
                            alt={p.category}
                            style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', border: '2px solid #334155' }}
                            onClick={() => setLightboxImage(`${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${p.id}/file`)}
                          />
                          <button onClick={() => handleDelete(p.id)} style={{ display: 'block', width: '80px', marginTop: '0.3rem', background: '#dc2626', color: 'white', border: 'none', padding: '0.2rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>Delete</button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {lightboxImage && (
        <div className="lightbox-overlay" onClick={() => setLightboxImage(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, cursor: 'pointer' }}>
          <button className="lightbox-close" onClick={() => setLightboxImage(null)} style={{ position: 'absolute', top: '20px', right: '30px', background: 'none', border: 'none', color: 'white', fontSize: '3rem', cursor: 'pointer' }}>&times;</button>
          <div className="lightbox-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh' }}>
            <img src={lightboxImage} alt="Fullscreen Example" style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px' }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamplePhotoManager;

